import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Activity, Layers, Building2, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email.trim(), password);
      navigate(data.is_platform_admin ? "/platform" : "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      {/* Brand / info panel */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-blue-600 grid place-items-center font-display font-bold text-xl">M</div>
            <div>
              <div className="font-display text-xl font-semibold">Mediva Solutions</div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Hospital Operations Platform</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-8 max-w-md">
          <h1 className="font-display text-4xl font-bold leading-tight">One platform for every facility in your healthcare group.</h1>
          <p className="text-slate-400 text-sm leading-relaxed">Multi-tenant foundation for OPD, Emergency, IPD, ICU, Pharmacy, Laboratory, Billing, PM-JAY and more — with strict tenant isolation and role-based access.</p>
          <div className="grid grid-cols-2 gap-4 pt-2">
            {[["Multi-tenant", Building2], ["16 modules", Layers], ["RBAC engine", ShieldCheck], ["Audit trail", Activity]].map(([t, Ic]) => (
              <div key={t} className="flex items-center gap-2.5 text-sm text-slate-300">
                <span className="h-8 w-8 rounded-md bg-slate-800 grid place-items-center"><Ic className="h-4 w-4 text-blue-400" /></span>{t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-[11px] font-mono text-slate-600">© 2026 Mediva Solutions</div>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-blue-600/10 blur-2xl" />
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-blue-600 grid place-items-center text-white font-display font-bold">M</div>
            <span className="font-display font-semibold text-slate-900">Mediva Solutions</span>
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Access your hospital operations console. <a href="/portal/login" className="text-blue-600 hover:underline" data-testid="patient-portal-link">Are you a patient?</a></p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="you@hospital.health" data-testid="login-email-input" autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                placeholder="••••••••" data-testid="login-password-input" autoComplete="current-password" />
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800" disabled={loading} data-testid="login-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Sign in</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
